import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { useQueueMonitor } from '../../hooks/useQueueMonitor';
import { useLiveExactLimits } from '../../hooks/useLiveExactLimits';
import { EnvironmentHealth, getEnvironmentHealth, WorkerBuild, WorkerStatus } from '../../api/health';
import { QueuePipeline, QueueState } from '../../api/queueMonitor';
import { getIssues, IssueList } from '../../api/issues';
import { getPostponed, Postponed } from '../data/cockpit';
import { envKind, exactTone as exactToneOf, usCount, useCockpit } from '../data/CockpitContext';
import { ago, clock, number, shortBuild, until } from '../data/format';
import { usePoll } from '../data/usePoll';
import { Card, Empty, Freshness, Kv, Loading, Meter, OwnerChip } from '../components/ui';
import { LiveObjects, usePassing } from '../components/LiveObjects';
import { recentlyPassed } from '../data/passing';
import { v2 } from '../paths';
import { ownerOfGroup } from './issueModel';
import { PoisonDrawer } from './PoisonDrawer';

interface Direction {
    queue: number;
    retry: number;
    poison: number;
    intake: number;
    perMinute?: number | null;
}

const direction = (queues: QueueState[] | undefined, pipeline: QueuePipeline): Direction => {
    const of = (stage: QueueState['stage']) =>
        (queues ?? []).filter((q) => q.pipeline === pipeline && q.stage === stage && q.depth >= 0);
    const sum = (list: QueueState[]) => list.reduce((n, q) => n + q.depth, 0);
    const ready = of('Ready');
    return {
        queue: sum(ready) + sum(of('Retry')),
        retry: sum(of('Retry')),
        poison: sum(of('Poison')),
        intake: sum(of('Webhook')),
        perMinute: ready.reduce<number | null>((n, q) => (q.perMinute === null || q.perMinute === undefined ? n : (n ?? 0) + q.perMinute), null),
    };
};

const heartbeat = (w?: WorkerStatus): 'ok' | 'warn' | 'off' =>
    !w ? 'off' : w.verdict === 'Running' || w.verdict === 'Alive' ? 'ok' : w.verdict === 'Silent' ? 'warn' : 'off';

/**
 * One connector app on the flow. Its heartbeat is read from the queue account's azure-webjobs-hosts; several customers'
 * apps keep that state in their own storage, where only leftovers from long ago remain (coosto prod: "509 d"). So a
 * heartbeat older than a day is not shown as one: the build the app itself reported says it runs, and without either
 * the chip says the heartbeat is out of sight rather than that the app is down.
 */
const AppChip = ({ name, worker, build, elsewhere, arrow, note, tone }: { name: string; worker?: WorkerStatus; build?: WorkerBuild; elsewhere?: boolean; arrow: 'r' | 'l'; note?: string; tone?: 'warn' | 'bad' }) => {
    const hb = heartbeat(worker);
    const recent = worker?.lastSeenAt && Date.now() - new Date(worker.lastSeenAt).getTime() < 86400000;
    const said = recent
        ? `gezien ${ago(worker!.lastSeenAt!)} geleden`
        : build?.reportedAt
          ? `build ${shortBuild(build.buildNumber) || build.commit?.slice(0, 8) || '?'} · ${ago(build.reportedAt)} geleden`
          : elsewhere
            ? 'hartslag niet zichtbaar'
            : 'geen hartslag';
    const title = recent
        ? worker?.evidence
        : build?.reportedAt
          ? `De app meldde zelf build ${build.buildNumber ?? build.commit} om ${clock(build.reportedAt)}. Zijn hartslag staat in een ander storage account dan dit dashboard leest.`
          : 'De hartslag van deze app staat in een ander storage account dan dit dashboard leest; dat zegt niets over of hij draait.';
    return (
        <span className={`app ${tone ?? (hb === 'warn' && recent ? 'warn' : '')}`} title={title}>
            {arrow === 'l' ? `← ${name}` : `${name} →`}
            <small>{note ?? said}</small>
        </span>
    );
};

export const EnvironmentPage = () => {
    const { customer, environment } = useParams();
    const axios = useAxiosSwagger();
    const { find } = useCockpit();
    const env = find(customer, environment);
    const [poisonOpen, setPoisonOpen] = useState(false);

    const queues = useQueueMonitor(customer, environment);
    const limits = useLiveExactLimits(customer, environment);
    const health = usePoll<EnvironmentHealth>(axios && customer && environment ? () => getEnvironmentHealth(axios, customer, environment) : null, 30000, [axios, customer, environment], `health/${customer}/${environment}`);
    const postponed = usePoll<Postponed>(axios && customer && environment ? (s) => getPostponed(axios, customer, environment, s) : null, 15000, [axios, customer, environment], `postponed/${customer}/${environment}`);
    const issues = usePoll<IssueList>(axios && customer && environment ? (s) => getIssues(axios, { customer, environment, status: 'open' }, s) : null, 30000, [axios, customer, environment], `issues/${customer}/${environment}`);
    const passing = usePassing(customer, environment);

    if (!customer || !environment) return null;

    const kind = env ? envKind(env) : environment.toLowerCase().startsWith('prod') ? 'prod' : 'test';
    const snapshot = queues.snapshot;
    const heen = direction(snapshot?.queues, 'ChargebeeToExact');
    const terug = direction(snapshot?.queues, 'ExactToChargebee');
    const workers = health.data?.workers?.workers ?? [];
    const worker = (suffix: string) => workers.find((w) => (w.suffix ?? w.key).toLowerCase().includes(suffix));
    const builds = health.data?.workers?.builds ?? [];
    const buildOf = (suffix: string) => builds.find((b) => b.app.toLowerCase().endsWith(suffix));
    const elsewhere = !!health.data?.workers?.hostStateElsewhere;
    // Running: a heartbeat of today, or the app reported its build in the last quarter of an hour.
    const alive = (suffix: string) => {
        const w = worker(suffix);
        const b = buildOf(suffix);
        const fresh = (at?: string | null, ms = 86400000) => !!at && Date.now() - new Date(at).getTime() < ms;
        return fresh(w?.lastSeenAt) || fresh(b?.reportedAt, 15 * 60000);
    };
    const build = builds.find((b) => b.buildNumber)?.buildNumber ?? env?.build;

    const remaining = limits?.dailyRemaining ?? env?.exactRemaining;
    const limit = limits?.dailyLimit ?? env?.exactLimit;
    const usedPct = limit && remaining !== null && remaining !== undefined ? ((limit - remaining) / limit) * 100 : 0;
    const minuteLow = limits?.minutelyRemaining !== null && limits?.minutelyRemaining !== undefined && limits.minutelyRemaining < 10;
    const budgetTone = exactToneOf(env, usedPct);
    const exactTone = budgetTone || (minuteLow ? 'warn' : '');
    const post = postponed.data;
    const firstReason = post?.reasons?.[0];
    const connectorTone = heen.poison + terug.poison > 0 ? 'bad' : (post?.total ?? 0) > 0 || heen.retry > 0 ? 'warn' : '';

    // The dots move only while something goes that way: a queue holds it, it drains, or an object passed lately. A
    // test environment's queues are empty again within seconds, so their depth alone kept the way to Exact still
    // while the way back, which had no condition, always moved.
    const moving = (d: Direction, way: 'ToExact' | 'ToChargebee') =>
        d.intake > 0 || d.queue > 0 || (d.perMinute ?? 0) > 0 || recentlyPassed(passing.chips, way, Date.now());
    const heenMoves = moving(heen, 'ToExact');
    const terugMoves = moving(terug, 'ToChargebee');
    const cbrcvPipe = heenMoves ? '' : 'stop';
    const heenPipe = heenMoves ? (exactTone ? 'slow' : '') : 'stop';
    const terugPipe = `back ${terugMoves ? '' : 'stop'}`;

    const groups = (issues.data?.groups ?? []).filter((g) => g.open > 0).sort((a, b) => usCount(b) - usCount(a) || b.open - a.open);

    // Before the first snapshot every count is unknown, not zero.
    const n = (value: number | null | undefined) => (snapshot ? number(value) : '…');
    const poisonButton = (count: number, cls = '') =>
        snapshot && count > 0 ? (
            <button className={`linkish mono bad ${cls}`} onClick={() => setPoisonOpen(true)} title="Bekijk wat in poison staat">
                {number(count)}
            </button>
        ) : (
            <span className={`mono ${cls}`}>{n(count)}</span>
        );

    return (
        <>
            {poisonOpen && snapshot && (
                <PoisonDrawer customer={customer} environment={environment} kind={kind} queues={snapshot.queues} onClose={() => setPoisonOpen(false)} />
            )}

            <Card
                title="Live flow"
                right={
                    <span className="hrow" style={{ gap: 14, fontSize: 12 }}>
                        <span className="mut">build <span className="mono" style={{ color: '#E6EAF0' }} title={builds.map((b) => `${b.app}: ${b.buildNumber}`).join('\n')}>{shortBuild(build) || '–'}</span>{builds.some((b) => b.mixed) && <span className="warn"> gemengd</span>}</span>
                        <span className="mut">laatst in Exact <span style={{ color: '#E6EAF0' }}>{env?.lastSuccessAt ? `${ago(env.lastSuccessAt)} geleden` : '–'}</span></span>
                        <span className="mut"><span style={{ color: '#5CC8FF' }}>●</span> heen</span>
                        <span className="mut"><span style={{ color: '#B49CFF' }}>●</span> terug</span>
                        <span className="mut"><span style={{ color: '#FFB547' }}>●</span> remt</span>
                    </span>
                }
            >
                <div className="fmap">
                    <div className="fl">
                        <div className="sysn">
                            <div className="nt"><span className="g cb">CB</span>Chargebee<span className={`hb ${alive('cbrcv') ? 'ok' : 'off'}`} title={alive('cbrcv') ? 'cbrcv draait' : 'Geen recent teken van cbrcv'} /></div>
                            <div className="nv">{n(heen.intake)}</div>
                            <div className="mut" style={{ fontSize: 12 }}>webhooks in de intake</div>
                        </div>
                        <div className="edge">
                            <div className="wire">
                                <span className={`pipe ${cbrcvPipe}`}><i /><i style={{ animationDelay: '-1.2s' }} /></span>
                                <AppChip name="cbrcv" worker={worker('cbrcv')} build={buildOf('cbrcv')} elsewhere={elsewhere} arrow="r" />
                                <span className={`pipe ${cbrcvPipe}`}><i /></span>
                            </div>
                            <div className="wire">
                                <span className={`pipe ${terugPipe}`}><i /></span>
                                <AppChip name="cbsnd" worker={worker('cbsnd')} build={buildOf('cbsnd')} elsewhere={elsewhere} arrow="l" note={terug.poison ? `${terug.poison} in poison` : undefined} tone={terug.poison ? 'bad' : undefined} />
                                <span className={`pipe ${terugPipe}`}><i style={{ animationDelay: '-1s' }} /></span>
                            </div>
                        </div>
                        <div className={`sysn ${connectorTone}`} style={{ padding: 0, gap: 0 }}>
                            <div className="nt" style={{ padding: '10px 14px 8px' }}><span className="g cn">CN</span>Connector<span className={`hb ${connectorTone || 'ok'}`} /></div>
                            <div className="part">
                                <div className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Heen · naar Exact</div>
                                <Kv rows={[
                                    ['In queue', <span className={`mono ${heen.queue > 50 ? 'warn' : ''}`}>{n(heen.queue)}</span>],
                                    ['Uitgesteld', <span className="mono">{post ? number(post.total) : '–'}</span>],
                                    ['Poison', poisonButton(heen.poison)],
                                    ['Afvoer', <span className="mono">{heen.perMinute === null || heen.perMinute === undefined ? '–' : `${number(Math.round(heen.perMinute))}/min`}</span>],
                                ]} />
                                {firstReason && (
                                    <div className="mut" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.35 }}>
                                        Uitgesteld: {firstReason.reason}
                                        {firstReason.nextAttemptAt ? `, weer om ${clock(firstReason.nextAttemptAt)}` : ''}
                                    </div>
                                )}
                            </div>
                            <div className="divider" />
                            <div className="part" style={{ paddingTop: 10 }}>
                                <div className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Terug · naar Chargebee</div>
                                <Kv rows={[
                                    ['In queue', <span className="mono">{n(terug.queue)}</span>],
                                    ['Poison', poisonButton(terug.poison)],
                                ]} />
                            </div>
                        </div>
                        <div className="edge">
                            <div className="wire">
                                <span className={`pipe ${heenPipe}`}><i /></span>
                                <AppChip name="exsnd" worker={worker('exsnd')} build={buildOf('exsnd')} elsewhere={elsewhere} arrow="r" note={minuteLow ? 'remt: minuutlimiet' : undefined} tone={minuteLow ? 'warn' : undefined} />
                                <span className={`pipe ${heenPipe}`}><i style={{ animationDelay: '-3s' }} /></span>
                            </div>
                            <div className="wire">
                                <span className={`pipe ${terugPipe}`}><i /></span>
                                <AppChip name="exrcv" worker={worker('exrcv')} build={buildOf('exrcv')} elsewhere={elsewhere} arrow="l" />
                                <span className={`pipe ${terugPipe}`}><i style={{ animationDelay: '-1.4s' }} /></span>
                            </div>
                        </div>
                        <div className={`sysn ${exactTone}`}>
                            <div className="nt"><span className="g ex">EX</span>Exact<span className={`hb ${exactTone || 'ok'}`} /></div>
                            <div className={`nv ${exactTone}`}>{number(remaining)}</div>
                            <div className="mut" style={{ fontSize: 12 }}>calls over vandaag</div>
                            <Meter pct={usedPct} tone={exactTone === 'bad' ? 'r' : exactTone === 'warn' ? 'w' : ''} width="100%" />
                            {limits?.minutelyRemaining !== undefined && limits?.minutelyRemaining !== null && (
                                <div className="mut" style={{ fontSize: 11 }}>minuut: {limits.minutelyRemaining} van {limits.minutelyLimit ?? 60}</div>
                            )}
                            <div className={env?.exactRunsOutAt ? 'bad' : 'mut'} style={{ fontSize: 11 }}>{env?.exactRunsOutAt ? `op over ${until(env.exactRunsOutAt)} bij dit tempo` : env?.exactUsedLastHour ? 'haalt het einde van de dag' : 'geen prognose'}</div>
                        </div>
                    </div>
                    <LiveObjects chips={passing.chips} error={passing.error} />
                </div>
                <Freshness
                    items={[
                        { label: queues.connection === 'live' ? 'Queues · live' : `Queues · ${queues.connection}`, at: snapshot?.sampledAt, staleAfter: 30 },
                        { label: 'Onderweg', at: passing.readAt, staleAfter: 20 },
                        { label: 'Hartslag apps', at: health.data?.sampledAt, staleAfter: 90 },
                        { label: 'Exact-limieten', at: limits?.readAtUtc, staleAfter: 600 },
                        { label: 'Uitgesteld', at: postponed.loadedAt, staleAfter: 45 },
                    ]}
                />
            </Card>

            <div className="g2">
                <Card title="Issues hier" right={<Link className="btn sm ghost" to={v2.envIssues(customer, environment)}>Alle {issues.data?.open ?? ''}</Link>}>
                    {issues.pending && <Loading />}
                    {issues.error && <Empty>{issues.error}</Empty>}
                    {issues.data && groups.length === 0 && <Empty>Geen open issues.</Empty>}
                    {groups.slice(0, 6).map((g) => {
                        const owner = ownerOfGroup(g);
                        return (
                            <Link key={g.code} className="li" to={`${v2.envIssues(customer, environment)}?code=${encodeURIComponent(g.code)}`}>
                                <span className={`sev ${owner}`} />
                                <span>
                                    <span className="t">{g.title}</span>
                                    <span className="m"><OwnerChip owner={owner} /><span className="mono">{g.open} objecten</span>{g.new > 0 && <span className="chip warn" style={{ padding: '0 6px' }}>{g.new} nieuw</span>}</span>
                                </span>
                                <span className="btn sm">Open</span>
                            </Link>
                        );
                    })}
                </Card>
                <Card title="Uitgesteld" right={post ? <span className="mut" style={{ fontSize: 12 }}>{post.total} berichten</span> : undefined}>
                    {postponed.pending && <Loading />}
                    {postponed.error && <Empty>{postponed.error}</Empty>}
                    {post && post.total === 0 && <Empty>Niets uitgesteld.</Empty>}
                    <div className="cb checks">
                        {(post?.reasons ?? []).map((r) => (
                            <div key={r.reason} className="chk">
                                <span className="ic wait">›</span>
                                <span>{r.reason}</span>
                                <span className="v">
                                    {r.count} · {r.nextAttemptAt ? `weer ${clock(r.nextAttemptAt)}` : '–'}
                                </span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </>
    );
};
